<template>
  <asc-card class="asc-mxrec-status-wrapper" flat>
    <template #title> {{ getTitle }}</template>
    <v-row no-gutters>
      <v-col class="d-flex flex-column form-wrapper">
        <asc-autocomplete-selector
          v-model="getStatusId"
          no-array
          :disabled="isDisabled"
          :items="getStatusOptions"
          :locked-items="getLockedStatusOptions"
          :readonly="isReadOnlyPermissionInvestigate"
          placeholder="Select a status"
          label="Status"
          :display-group-name="false"
          :sort-items="false"
          class="mt-0"
        >
          <template slot="footer">
            <asc-info-label
              v-if="showWarning"
              class="asc-warning-label"
              small
              type="warning"
              value="Selected status is not valid for Mx Rec Submission."
            />
          </template>
        </asc-autocomplete-selector>
        <asc-autocomplete-selector
          v-model="getOwnerUserId"
          placeholder="Alert owner"
          label="Alert Owner"
          no-array
          :readonly="isReadOnlyPermissionInvestigate"
          :disabled="isDisabled"
          :items="ownerNameOptions"
          :item-height="40"
          :detail-row-prop="`email`"
          :sort-items="false"
          :icon="menuIcon"
        />
        <asc-autocomplete-selector
          v-if="isPersonWhoSentVisible"
          v-model="getPersonWhoSentId"
          placeholder="Person Who Sent Mx Rec"
          label="Person Who Sent Mx Rec"
          no-array
          :readonly="isReadOnlyPermissionInvestigate"
          :disabled="isDisabled"
          :item-height="40"
          :items="ownerNameOptions"
          :detail-row-prop="`email`"
          :sort-items="false"
          :icon="menuIcon"
        />
        <asc-text-field-input
          v-model="getAssetSupportPOC"
          label="Asset Support POC"
          :max-length="textFieldMaxLength"
          :disabled="isDisabled"
          :readonly="isReadOnlyPermissionInvestigate"
          :hide-details="isSourceVisible ? false : true"
        />
        <asc-text-field-input
          v-if="isAirlineTier3OrTier4"
          :value="getIssueSource"
          label="Mx Rec Source"
          :readonly="true"
          hide-details
        />
      </v-col>
    </v-row>
  </asc-card>
</template>

<script>
import AscCard from '../atoms/AscCard.vue'
import AscTextFieldInput from '../atoms/AscTextFieldInput.vue'
import AscAutocompleteSelector from '../molecules/AscAutocompleteSelector.vue'
import AscInfoLabel from '../molecules/AscInfoLabel.vue'

import AscMxRecPropsMixin from '../mixins/AscMxRecPropsMixin.vue'
import AscMxRecPermissionMixin from '../mixins/AscMxRecPermissionMixin.vue'

import { COLLINS_PHM_TIER_3, COLLINS_PHM_TIER_4 } from '@/utilities/appConsts'

export default {
  name: 'AscMxRecStatusWrapper',

  components: {
    AscAutocompleteSelector,
    AscInfoLabel,
    AscCard,
    AscTextFieldInput,
  },

  mixins: [AscMxRecPropsMixin, AscMxRecPermissionMixin],

  props: {
    statusOptions: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Object,
      default: () => {},
    },
    investigationUsersToTag: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showWarning: {
      type: Boolean,
      default: false,
    },
    statusId: {
      type: Array,
      default: () => [],
    },
    isPreSubmittal: {
      type: Boolean,
      default: false,
    },
    lockedItems: {
      type: Array,
      default: () => [],
    },
    issueSource: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      menuIcon: 'mdi-account',
      textFieldMaxLength: 100,
      // permissionLevelConsts: PERMISSION_LEVEL_CONSTS,
      // resources: RESOURCES,
    }
  },

  computed: {
    getIssueSource() {
      return this.issueSource.name
    },
    isDisabled() {
      return this.disabled
    },

    getStatusOptions: {
      get() {
        // need to increment the value for all the status options
        // since you can't use 0 for a value
        const incrementedStatusOptions = this.statusOptions.map(statusObj => {
          return {
            ...statusObj,
            value: statusObj.value + 1,
          }
        })
        const sortedStatusOptions = incrementedStatusOptions.sort((a, b) =>
          a.sortOrder > b.sortOrder ? 1 : -1,
        )
        return sortedStatusOptions
      },
    },
    getLockedStatusOptions() {
      // since we are incrementing all the status values above (so you can't have an item with 0)
      // be sure to increment the status values of any locked options
      return this.lockedItems.map(value => value + 1)
    },
    getOwnerUserId: {
      get() {
        return this.data.ownerUserId
      },
      set(val) {
        this.emitChangeEvent({
          ownerUserId: val,
        })
      },
    },
    getPersonWhoSentId: {
      get() {
        return this.data.personWhoSentMxRec
      },
      set(val) {
        this.emitChangeEvent({
          personWhoSentMxRec: val,
        })
      },
    },
    getAssetSupportPOC: {
      get() {
        return this.data.assetSupportPOC
      },
      set(val) {
        this.emitChangeEvent({
          assetSupportPOC: val,
        })
      },
    },
    getStatusId: {
      get() {
        const whichIndex = this.statusOptions.filter(statusOptionObj => {
          return statusOptionObj.id === this.data.statusId
        })
        if (whichIndex.length > 0) {
          // need to return the value incremented by 1 here since we had
          // to increment all the values of the statuses
          return whichIndex[0].value + 1
        } else {
          return null
        }
      },
      set(val) {
        const whichIndex = this.statusOptions.filter(statusOptionObj => {
          return statusOptionObj.value + 1 === val
        })
        if (whichIndex.length > 0) {
          this.handleStatusChange(whichIndex[0].id)
        } else {
          this.handleStatusChange(null)
        }
      },
    },
    ownerNameOptions() {
      return this.investigationUsersToTag.map(user => {
        return {
          email: user.userName,
          internal: true,
          text: `${user.firstName} ${user.lastName}`,
          value: user.userId,
        }
      })
    },

    isPersonWhoSentVisible() {
      return this.airlineTierTypeId === COLLINS_PHM_TIER_3 || COLLINS_PHM_TIER_4
        ? true
        : false
    },

    isSourceVisible() {
      // No need to check for issue investigator/updater and reader role as
      // ascMxRecStatusWrapper is only available for issue investigator role
      return this.airlineTierTypeId === COLLINS_PHM_TIER_3 ? true : false
    },
    isAirlineTier3OrTier4() {
      return this.airlineTierTypeId === COLLINS_PHM_TIER_3 || COLLINS_PHM_TIER_4
        ? true
        : false
    },

    getTitle() {
      return this.airlineTierTypeId === COLLINS_PHM_TIER_3 || COLLINS_PHM_TIER_4
        ? 'ALERT INFORMATION'
        : 'STATUS & POCS'
    },
  },

  methods: {
    emitChangeEvent(propToOverwrite) {
      const mergedInfo = {
        ...this.data,
        ...propToOverwrite,
      }
      this.$emit('updateStatusInfo', mergedInfo)
    },
    // this is specifically for changes to the mx rec status since
    // there is other logic around that
    handleStatusChange(val) {
      this.$emit('updateStatus', val)
    },
  },
}
</script>

<style lang="scss">
.asc-mxrec-status-wrapper {
  height: 100%;

  .form-wrapper {
    .asc-autocomplete-selector {
      margin-bottom: 21px;

      .asc-base-selector--label {
        height: 12px;
        margin-top: 4px;

        .asc-required-label {
          line-height: 12px;
        }
      }

      .asc-base-selector--input-container {
        height: 32px !important;
        padding: 8px 0 8px;
        align-items: center;

        .asc-base-selector--input {
          height: 24px;
        }
      }

      & .asc-selector--list-item-label {
        width: 100% !important;
        display: block;
        padding: 2px 12px 0 8px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .asc-autocomplete-selector-options--container
        .asc-selector--options-container
        .asc-selector--available-list-container {
        max-height: 355px;
      }
    }
  }
}
</style>
